import { useEffect, useRef, useSyncExternalStore } from 'react';
import { notification as toast } from 'antd';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { getEcho, getRealtimeStatus, subscribeRealtimeStatus } from '../services/realtime';
import browserNotifications from '../services/notificationService';

export const INBOX_KEY = ['notification-inbox'];
export const HISTORY_KEY = ['notification-history'];

/**
 * Listens on the user's private channel and drops each new notification straight into the bell's
 * cached inbox, with a small toast (and a browser notification when the tab is in the background).
 * Returns the connection status, so callers can poll only while live updates are down.
 */
export default function useLiveNotifications(userId) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const status = useSyncExternalStore(subscribeRealtimeStatus, getRealtimeStatus);
  const wasDown = useRef(false);

  useEffect(() => {
    if (!userId) return undefined;
    const echo = getEcho();
    if (!echo) return undefined;

    const channelName = `App.Models.User.${userId}`;
    echo.private(channelName).listen('.notification.created', ({ item }) => {
      if (!item) return;

      queryClient.setQueryData(INBOX_KEY, (inbox) => {
        if (!inbox) return inbox;
        if (inbox.items.some((existing) => existing.id === item.id)) return inbox;
        const counts = { ...(inbox.counts || {}) };
        counts.unread_total = (counts.unread_total || 0) + 1;
        counts[item.category] = (counts[item.category] || 0) + 1;
        return { ...inbox, unread_count: (inbox.unread_count || 0) + 1, counts, items: [item, ...inbox.items].slice(0, 40) };
      });
      queryClient.invalidateQueries({ queryKey: HISTORY_KEY });

      const open = () => item.url && navigate(item.url, item.url_state ? { state: item.url_state } : undefined);
      toast.open({
        key: item.id,
        title: item.title,
        description: item.message,
        placement: 'bottomRight',
        duration: item.priority === 'critical' ? 0 : 6,
        onClick: () => { open(); toast.destroy(item.id); },
        style: { cursor: item.url ? 'pointer' : 'default' },
      });
      if (document.hidden && browserNotifications.isEnabled()) {
        browserNotifications.showGeneric(item.title, item.message, item.url);
      }
    });

    return () => echo.leave(channelName);
  }, [userId, queryClient, navigate]);

  // After the connection comes back, fetch once: anything sent while it was down was not pushed.
  useEffect(() => {
    if (status === 'unavailable') wasDown.current = true;
    if (status === 'connected' && wasDown.current) {
      wasDown.current = false;
      queryClient.invalidateQueries({ queryKey: INBOX_KEY });
      queryClient.invalidateQueries({ queryKey: HISTORY_KEY });
    }
  }, [status, queryClient]);

  return status;
}
